import { useState } from 'react';
import type { SequenceDocument } from '../../format/types';

interface PatternListProps {
  readonly patterns: readonly SequenceDocument[];
  readonly activePatternId: string;
  readonly onSelect: (patternId: string) => void;
  readonly onNew: () => void;
  readonly onDuplicate: (patternId: string) => void;
  readonly onRename: (patternId: string, name: string) => void;
  readonly onDelete: (patternId: string) => void;
}

export function PatternList(props: PatternListProps): JSX.Element {
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [draftName, setDraftName] = useState('');

  function startEditing(pattern: SequenceDocument): void {
    setEditingId(pattern.id);
    setDraftName(pattern.name);
  }

  function commitEditing(): void {
    if (editingId) {
      props.onRename(editingId, draftName.trim() || 'Unbenannt');
    }
    setEditingId(undefined);
  }

  return (
    <div className="pattern-bank" role="tablist" aria-label="Pattern-Bank">
      {props.patterns.map((pattern) => {
        const isActive = pattern.id === props.activePatternId;
        return (
          <div key={pattern.id} className={`pattern-chip${isActive ? ' active' : ''}`}>
            {editingId === pattern.id ? (
              <input
                autoFocus
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                onBlur={commitEditing}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitEditing();
                  if (event.key === 'Escape') setEditingId(undefined);
                }}
                aria-label="Pattern-Name bearbeiten"
              />
            ) : (
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => props.onSelect(pattern.id)}
                onDoubleClick={() => startEditing(pattern)}
                title="Doppelklick zum Umbenennen"
              >
                {pattern.name}
              </button>
            )}
            <button
              type="button"
              className="icon-button"
              onClick={() => props.onDuplicate(pattern.id)}
              aria-label={`${pattern.name} duplizieren`}
              title="Duplizieren"
            >
              ⧉
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => props.onDelete(pattern.id)}
              disabled={props.patterns.length <= 1}
              aria-label={`${pattern.name} loeschen`}
              title="Loeschen"
            >
              ✕
            </button>
          </div>
        );
      })}
      <button type="button" className="new-pattern" onClick={props.onNew} aria-label="Neues Pattern anlegen">
        + Neues Pattern
      </button>
    </div>
  );
}
